import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { AlertTriangle, Plus } from 'lucide-react'
import { useParams } from 'react-router'
import {
  CurrencyValue, ErrorState, FileUploader, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import type { UploadedFile } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useExpenseClaim, useExpenseMutations, useExpenseTypes } from '../api'
import { CLAIM_STATUS_LABELS, REIMBURSEMENT_METHOD_LABELS } from '../types'
import type { ClaimStatus, ReimbursementMethod } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<ClaimStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'info',
  PARTIALLY_APPROVED: 'warning',
  REJECTED: 'danger',
  REIMBURSED: 'success',
  CANCELLED: 'neutral',
}

/**
 * One claim, its lines, and the decision on it.
 *
 * The approved total is a separate field from the claimed one throughout, because approving
 * part of a claim is a normal outcome and overwriting what was asked for loses the
 * conversation that produced the number.
 */
export function ExpenseClaimDetailPage() {
  const { id } = useParams<{ id: string }>()
  const claim = useExpenseClaim(id)
  useBreadcrumb(claim.data?.reference ?? 'Claim')
  const types = useExpenseTypes()
  const { addLine, submitClaim, approveClaim, rejectClaim, reimburse } = useExpenseMutations()

  const [lineDraft, setLineDraft] = useState<{
    expenseTypeId: string; spentOn: string; description: string
    amountClaimed: string; receipt: UploadedFile | null
  } | null>(null)
  const [decision, setDecision] = useState<{ approvedTotal: string; comment: string } | null>(null)
  const [payment, setPayment] = useState<{
    method: ReimbursementMethod; reimbursedOn: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (claim.isPending) return <Skeleton className="h-64" />
  if (claim.isError) return <ErrorState error={claim.error} onRetry={() => void claim.refetch()} />
  if (!claim.data) return null

  const current = claim.data
  const status = current.status as ClaimStatus
  const draftable = status === 'DRAFT'
  const decidable = status === 'SUBMITTED'
  const payable = status === 'APPROVED' || status === 'PARTIALLY_APPROVED'
  const requiresReceipt = types.data?.find(
    (type) => type.id === lineDraft?.expenseTypeId)?.requiresReceipt ?? false

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${current.reference} — ${current.title}`}
        subtitle={`${current.employeeName ?? 'Unknown person'} · claimed ${current.claimDate}`}
        actions={
          <div className="flex flex-wrap gap-2">
            {draftable && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setError(null)
                    setLineDraft({
                      expenseTypeId: types.data?.[0]?.id ?? '', spentOn: current.claimDate,
                      description: '', amountClaimed: '', receipt: null,
                    })
                  }}
                >
                  <Plus className="size-4" aria-hidden="true" />
                  Add line
                </Button>
                <Button
                  type="button"
                  disabled={current.lines.length === 0 || submitClaim.isPending}
                  onClick={() => {
                    void (async () => {
                      setError(null)
                      try {
                        await submitClaim.mutateAsync(current.id)
                      } catch (caught) {
                        setError(toApiProblem(caught)?.detail ?? 'That claim could not be submitted')
                      }
                    })()
                  }}
                >
                  Submit for approval
                </Button>
              </>
            )}
            {decidable && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setError(null)
                    void (async () => {
                      try {
                        await rejectClaim.mutateAsync({ id: current.id })
                      } catch (caught) {
                        setError(toApiProblem(caught)?.detail ?? 'That claim could not be rejected')
                      }
                    })()
                  }}
                >
                  Reject
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setError(null)
                    setDecision({ approvedTotal: String(current.totalClaimed), comment: '' })
                  }}
                >
                  Approve
                </Button>
              </>
            )}
            {payable && (
              <Button
                type="button"
                onClick={() => {
                  setError(null)
                  setPayment({
                    method: 'PAYROLL',
                    reimbursedOn: todayLocal(),
                  })
                }}
              >
                Record reimbursement
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <Card className="p-4">
        <dl className="grid gap-4 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-slate-600">Status</dt>
            <dd className="mt-1">
              <StatusBadge
                status={current.status}
                tone={STATUS_TONE[status] ?? 'neutral'}
                label={CLAIM_STATUS_LABELS[status] ?? current.status}
              />
            </dd>
          </div>
          <div>
            <dt className="text-slate-600">Claimed</dt>
            <dd className="mt-1 font-medium">
              <CurrencyValue value={current.totalClaimed} currency={current.currency} />
            </dd>
          </div>
          <div>
            <dt className="text-slate-600">Approved</dt>
            <dd className="mt-1 font-medium">
              <CurrencyValue value={current.totalApproved} currency={current.currency} />
            </dd>
          </div>
          <div>
            <dt className="text-slate-600">Paid back</dt>
            <dd className="mt-1">
              {current.reimbursedOn
                ? `${current.reimbursedOn} — ${
                    REIMBURSEMENT_METHOD_LABELS[
                      current.reimbursementMethod as ReimbursementMethod] ??
                      current.reimbursementMethod}`
                : 'Not yet'}
            </dd>
          </div>
        </dl>
        {current.decisionComment && (
          <p className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            {current.decisionComment}
          </p>
        )}
      </Card>

      <Card>
        <div className="overflow-x-auto overscroll-x-contain">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-3 text-start font-medium">Type</th>
                <th className="p-3 text-start font-medium">Spent on</th>
                <th className="p-3 text-start font-medium">Description</th>
                <th className="p-3 text-end font-medium">Amount</th>
                <th className="p-3 text-start font-medium">Against policy</th>
                <th className="p-3 text-start font-medium">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {current.lines.length === 0 && (
                <tr>
                  <td className="p-4 text-slate-600" colSpan={6}>
                    Nothing on this claim yet. It cannot be submitted while it is empty.
                  </td>
                </tr>
              )}
              {current.lines.map((line) => (
                <tr key={line.id} className="border-b border-slate-100">
                  <td className="p-3">{line.expenseTypeName}</td>
                  <td className="p-3 text-slate-600">{line.spentOn}</td>
                  <td className="p-3 text-slate-600">{line.description ?? '—'}</td>
                  <td className="p-3 text-end">
                    <CurrencyValue value={line.amountClaimed} currency={line.currency} />
                  </td>
                  <td className="p-3">
                    {line.overLimitBy != null ? (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <AlertTriangle className="size-3.5" aria-hidden="true" />
                        {line.overLimitBy} over the {line.limitApplied} limit
                      </span>
                    ) : line.limitApplied != null ? (
                      <span className="text-slate-600">Within the {line.limitApplied} limit</span>
                    ) : (
                      <span className="text-slate-600">No limit set</span>
                    )}
                  </td>
                  <td className="p-3 text-slate-600">
                    {line.receiptFileId ? 'Attached' : 'None'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <FormDialog
        open={lineDraft != null}
        onOpenChange={(open) => { if (!open) setLineDraft(null) }}
        size="lg"
        title="Add a line"
        description="Over-policy lines are accepted and flagged for the approver, not refused."
        submitLabel="Add line"
        loading={addLine.isPending}
        error={error}
        onSubmit={() => {
          if (!lineDraft) return
          void (async () => {
            setError(null)
            try {
              await addLine.mutateAsync({
                id: current.id,
                body: {
                  expenseTypeId: lineDraft.expenseTypeId,
                  spentOn: lineDraft.spentOn,
                  description: lineDraft.description || null,
                  amountClaimed: Number(lineDraft.amountClaimed),
                  currency: current.currency,
                  receiptFileId: lineDraft.receipt?.id ?? null,
                },
              })
              setLineDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That line could not be added')
            }
          })()
        }}
      >
        {lineDraft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="line-type">Expense type</Label>
                <Select
                  id="line-type"
                  className="w-full"
                  value={lineDraft.expenseTypeId}
                  onChange={(event) =>
                    setLineDraft({ ...lineDraft, expenseTypeId: event.target.value })}
                >
                  <option value="">Choose a type…</option>
                  {types.data?.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="line-date">Spent on</Label>
                <DatePicker
                  id="line-date" value={lineDraft.spentOn}
                  onChange={(event) => setLineDraft({ ...lineDraft, spentOn: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="line-amount">Amount</Label>
                <Input
                  id="line-amount" type="number" value={lineDraft.amountClaimed}
                  onChange={(event) =>
                    setLineDraft({ ...lineDraft, amountClaimed: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="line-description">Description</Label>
                <Input
                  id="line-description" value={lineDraft.description}
                  onChange={(event) =>
                    setLineDraft({ ...lineDraft, description: event.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Receipt</Label>
              {requiresReceipt && (
                <p className="mb-2 text-xs text-amber-700">
                  This type needs a receipt — the line will be refused without one.
                </p>
              )}
              <FileUploader
                ownerType="EXPENSE_CLAIM"
                ownerId={current.id}
                files={lineDraft.receipt ? [lineDraft.receipt] : []}
                onUploaded={(file) => setLineDraft({ ...lineDraft, receipt: file })}
                onRemoved={() => setLineDraft({ ...lineDraft, receipt: null })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={decision != null}
        onOpenChange={(open) => { if (!open) setDecision(null) }}
        title="Approve this claim"
        description="Approving less than was claimed is recorded as a partial approval, with the difference visible."
        submitLabel="Approve"
        loading={approveClaim.isPending}
        error={error}
        onSubmit={() => {
          if (!decision) return
          void (async () => {
            setError(null)
            try {
              await approveClaim.mutateAsync({
                id: current.id,
                approvedTotal: decision.approvedTotal ? Number(decision.approvedTotal) : undefined,
                comment: decision.comment || undefined,
              })
              setDecision(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That claim could not be approved')
            }
          })()
        }}
      >
        {decision && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="decision-total">Approved total</Label>
              <Input
                id="decision-total" type="number" value={decision.approvedTotal}
                onChange={(event) =>
                  setDecision({ ...decision, approvedTotal: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Claimed: {current.totalClaimed} {current.currency}.
              </p>
            </div>
            <div>
              <Label htmlFor="decision-comment">Comment</Label>
              <Input
                id="decision-comment" value={decision.comment}
                onChange={(event) => setDecision({ ...decision, comment: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={payment != null}
        onOpenChange={(open) => { if (!open) setPayment(null) }}
        title="Record the reimbursement"
        description="Through payroll it appears on the next run's payslip; paid directly it does not."
        submitLabel="Record"
        loading={reimburse.isPending}
        error={error}
        onSubmit={() => {
          if (!payment) return
          void (async () => {
            setError(null)
            try {
              await reimburse.mutateAsync({
                id: current.id,
                method: payment.method,
                reimbursedOn: payment.reimbursedOn,
              })
              setPayment(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That reimbursement could not be recorded')
            }
          })()
        }}
      >
        {payment && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="pay-method">How it was paid</Label>
              <Select
                id="pay-method"
                className="w-full"
                value={payment.method}
                onChange={(event) =>
                  setPayment({ ...payment, method: event.target.value as ReimbursementMethod })}
              >
                {Object.entries(REIMBURSEMENT_METHOD_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="pay-date">Paid on</Label>
              <DatePicker
                id="pay-date" value={payment.reimbursedOn}
                onChange={(event) => setPayment({ ...payment, reimbursedOn: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
